import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, TextInput, Image, ScrollView, Animated, Easing, Alert, Clipboard } from 'react-native';
import debounce from 'lodash/debounce';
import { LinearGradient } from 'expo-linear-gradient';
import * as ImagePicker from 'expo-image-picker';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from '../components/NavigationHeader';
import { BlurView } from 'expo-blur';
import { ScreenBackground } from '../components/ThemeGradients';
import { GlassBackgroundGradient, GradientOverlay } from '../components/Gradients';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { useLoading } from '../context/LoadingContext';
import { useBackgroundQuery } from '../context/BackgroundQueryContext';
import { useTicker, AVAILABLE_TICKER_OPTIONS, DEFAULT_TICKER_LEAGUES, SPORT_CATEGORIES, getLeaguesBySport, TICKER_NAME_FORMATS } from '../context/TickerContext';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, orderBy, query as fsQuery, where, getDocs, deleteDoc, doc, limit as fsLimit, startAfter, getCountFromServer } from 'firebase/firestore';
import { authService } from '../services/authService';
import * as DocumentPicker from 'expo-document-picker';
import { getAuth } from 'firebase/auth';
import { ALL_SPORTSBOOKS, SPORTSBOOK_CATEGORIES, getSportsbookName } from '../data/sportsData';
import { useFeedback } from '../context/FeedbackContext';
import { openBillingPortal } from '../services/stripeService';
import ProfileResetPasswordSection from '../components/ProfileResetPasswordSection';
import ProfileBooksPanelSection from '../components/ProfileBooksPanelSection';
import ToggleSwitch from '../components/ToggleSwitch';
import ProfileTickerSection from '../components/ProfileTickerSection';
import UsageSubscriptionCard from '../components/UsageSubscriptionCard';
import { PLAN_META } from '../../utils/planMeta';
import { styles } from './profileStyles';
import ProfilePageView from '../components/ProfilePageView';
import { responsive } from '../utils/responsive';


const ProfilePage = () => {
    const {
        user,
        logout,
        userProfile,
        effectiveUserProfile,
        profileLoading,
        saveUserProfile,
        uploadProfilePhoto,
        removeProfilePhoto,
        adminPreviewPlan,
    } = useAuth();
    const { navigateTo, isAdmin, routePayload, currentScreen, dfsConsensusExclusionEnabled } = useNavigation();
    const { startLogout } = useLoading();
    const { openFeedbackFor } = useFeedback();
    const { isTickerEnabled, toggleTicker, totalHeaderHeight, tickerLeagues, updateTickerLeagues, tickerNameFormat, updateTickerNameFormat, showTickerSGPIndicator, updateShowTickerSGPIndicator, tickerSGPOnly, updateTickerSGPOnly, showLiveScores, updateShowLiveScores, isInjuryTickerEnabled, updateInjuryTickerEnabled, injuryTickerLeagues, updateInjuryTickerLeagues, injuryMinSeverity, updateInjuryMinSeverity, injuryGroupBy, updateInjuryGroupBy } = useTicker();
    const [saving, setSaving] = useState(false);
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
    const VALID_TABS = ['settings', 'betslips', 'savedQueries', 'watchedFixtures', 'queryHistory'];
    const HISTORY_PAGE_SIZE = 25;
    const [activeTab, setActiveTab] = useState('settings');
    const [activeSettingsSection, setActiveSettingsSection] = useState('profile');
    const [sbMode, setSbMode] = useState('mybooks'); // 'mybooks' | 'filter'

    // --- Paginated query history (loaded directly from Firestore, not from context) ---
    const [localHistory, setLocalHistory] = useState([]);
    const [localHistoryLoading, setLocalHistoryLoading] = useState(false);
    const [localHistoryHasMore, setLocalHistoryHasMore] = useState(false);
    const [localHistoryCursor, setLocalHistoryCursor] = useState(null);
    const [historyStats, setHistoryStats] = useState({ allTime: null, thisMonth: null, completed: null, avgResults: null });
    const [betSlips, setBetSlips] = useState([]);
    const [betSlipsLoading, setBetSlipsLoading] = useState(true);
    const [savedQueries, setSavedQueries] = useState([]);
    const [savedQueriesLoading, setSavedQueriesLoading] = useState(true);
    const [watchedFixtures, setWatchedFixtures] = useState([]);
    const [watchedFixturesLoading, setWatchedFixturesLoading] = useState(true);
    
    // Query usage tracking
    const [queryUsage, setQueryUsage] = useState({ queriesUsed: 0, queryLimit: 10, planType: 'FREE' });
    const [queryUsageLoading, setQueryUsageLoading] = useState(true);

    // Animation values
    const pageOpacity = useRef(new Animated.Value(0)).current;
    const pageTranslateY = useRef(new Animated.Value(30)).current;
    const [isExiting, setIsExiting] = useState(false);
    
    // Section animations
    const profileSectionOpacity = useRef(new Animated.Value(0)).current;
    const accountSectionOpacity = useRef(new Animated.Value(0)).current;
    const preferencesSectionOpacity = useRef(new Animated.Value(0)).current;
    const actionButtonsOpacity = useRef(new Animated.Value(0)).current;

    // User profile data
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [usernameStatus, setUsernameStatus] = useState(null);
    const usernameCheckTimer = useRef(null);
    const originalUsername = useRef('');
    const [phone, setPhone] = useState('');
    const [profilePhoto, setProfilePhoto] = useState('');
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);

    // Math settings
    const [kellyCriterion, setKellyCriterion] = useState('0.25');
    const [legsPerPage, setLegsPerPage] = useState('10');
    const [minBooksStraights, setMinBooksStraights] = useState('5');
    const [minBooksSGP, setMinBooksSGP] = useState('5');
    const [minBooksMyBooks, setMinBooksMyBooks] = useState('3');
    const [unitSize, setUnitSize] = useState('$10');

    // Toggle settings
    const [showAllMath, setShowAllMath] = useState(true);
    const [showNerdMathByDefault, setShowNerdMathByDefault] = useState(false);
    const [showAltsExpanded, setShowAltsExpanded] = useState(true); // ⭐ NEW: Show alternate lines expanded by default
    const [filterPositiveEvOnly, setFilterPositiveEvOnly] = useState(false); // Default off — hides ≤0 EV cards when on
    const [defaultBetMode, setDefaultBetMode] = useState('infer');
    const [autoSwitchMode, setAutoSwitchMode] = useState(true);
    const [defaultSortMethod, setDefaultSortMethod] = useState('ev'); // 'ev' or 'odds'
    const [defaultBookSort, setDefaultBookSort] = useState('ev'); // 'ev' | 'odds' | 'mybooks'
    const [backgroundQueryMode, setBackgroundQueryMode] = useState(false); // Background queries by default
    const [doneSoundEnabled, setDoneSoundEnabled] = useState(true); // Subtle chime on background query completion
    const [showEnvPill, setShowEnvPill] = useState(true);

    // Background query context
    const { queryHistory, historyLoading, deleteFromHistory, clearHistory, loadQueryHistory } = useBackgroundQuery();

    // Sportsbook filters (tag-like selection)
    const [selectedSportsbooks, setSelectedSportsbooks] = useState([]);
    const [showAllFilters, setShowAllFilters] = useState(false);
    const [sportsbookLimits, setSportsbookLimits] = useState({});
    const [enabledSportsbookLimits, setEnabledSportsbookLimits] = useState({});
    
    // My Books — preferred sportsbooks. Priority/prioritization in outer results
    // is now derived from `defaultBookSort === 'mybooks'` (single master switch);
    // there is no longer a standalone `myBooksPriority` toggle.
    const [myBooks, setMyBooks] = useState([]);
    const [myBooksSearch, setMyBooksSearch] = useState('');
    const [myBooksExpanded, setMyBooksExpanded] = useState({ major: true }); // Expanded categories
    const [filterBooksSearch, setFilterBooksSearch] = useState('');
    const [filterBooksExpanded, setFilterBooksExpanded] = useState({ major: true });

    // v1.15.x P3.4 — consensus exclusion preference.
    // `consensusBookPolicy` is one of 'default' | 'include_all' | 'custom'.
    // `consensusExcludedBooks` is only used when policy === 'custom'.
    const [consensusBookPolicy, setConsensusBookPolicy] = useState('default');
    const [consensusExcludedBooks, setConsensusExcludedBooks] = useState([]);
    const [consensusBooksSearch, setConsensusBooksSearch] = useState('');

    // --- Paginated Firestore history loaders ---
    const loadHistoryFirstPage = useCallback(async () => {
        if (!user?.uid) return;
        setLocalHistoryLoading(true);
        setLocalHistory([]);
        setLocalHistoryCursor(null);
        setLocalHistoryHasMore(false);
        setHistoryStats({ allTime: null, thisMonth: null, completed: null, avgResults: null });
        try {
            const colRef = collection(db, 'users', user.uid, 'query_history');
            const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

            // Parallel: first page + two count queries
            const pageQuery = fsQuery(colRef, orderBy('createdAt', 'desc'), fsLimit(HISTORY_PAGE_SIZE));
            const [snap, allCount, monthCount] = await Promise.all([
                getDocs(pageQuery),
                getCountFromServer(colRef),
                getCountFromServer(fsQuery(colRef, where('createdAt', '>=', monthStart))),
            ]);

            const docs = snap.docs.map(d => ({
                id: d.id, ...d.data(),
                createdAt: d.data().createdAt?.toDate?.() || null,
            }));

            const completedDocs = docs.filter(d => d.status === 'completed');
            const avgResults = completedDocs.length > 0
                ? Math.round(completedDocs.reduce((s, d) => s + (d.resultsCount || 0), 0) / completedDocs.length)
                : 0;

            setLocalHistory(docs);
            setLocalHistoryCursor(snap.docs[snap.docs.length - 1] || null);
            setLocalHistoryHasMore(docs.length === HISTORY_PAGE_SIZE);
            setHistoryStats({
                allTime: allCount.data().count,
                thisMonth: monthCount.data().count,
                completed: completedDocs.length,
                avgResults,
            });
        } catch (e) {
            console.error('[ProfilePage] history load error', e);
        } finally {
            setLocalHistoryLoading(false);
        }
    }, [user?.uid]);

    const loadHistoryNextPage = useCallback(async () => {
        if (!user?.uid || !localHistoryCursor || localHistoryLoading) return;
        setLocalHistoryLoading(true);
        try {
            const colRef = collection(db, 'users', user.uid, 'query_history');
            const q = fsQuery(colRef, orderBy('createdAt', 'desc'), startAfter(localHistoryCursor), fsLimit(HISTORY_PAGE_SIZE));
            const snap = await getDocs(q);
            const docs = snap.docs.map(d => ({
                id: d.id, ...d.data(),
                createdAt: d.data().createdAt?.toDate?.() || null,
            }));
            setLocalHistory(prev => [...prev, ...docs]);
            setLocalHistoryCursor(snap.docs[snap.docs.length - 1] || null);
            setLocalHistoryHasMore(docs.length === HISTORY_PAGE_SIZE);
        } catch (e) {
            console.error('[ProfilePage] history next page error', e);
        } finally {
            setLocalHistoryLoading(false);
        }
    }, [user?.uid, localHistoryCursor, localHistoryLoading]);

    const handleDeleteHistoryEntry = useCallback(async (entryId) => {
        if (!user?.uid) return;
        try {
            await deleteDoc(doc(db, 'users', user.uid, 'query_history', entryId));
            setLocalHistory(prev => prev.filter(e => e.id !== entryId));
            setHistoryStats(prev => ({ ...prev, allTime: prev.allTime != null ? prev.allTime - 1 : null }));
            // Also update context's in-memory list (for feedback pill recent queries)
            deleteFromHistory(entryId);
        } catch (e) {
            console.error('[ProfilePage] delete history error', e);
        }
    }, [user?.uid, deleteFromHistory]);

    const handleClearHistory = useCallback(() => {
        Alert.alert('Clear History', 'Are you sure you want to clear all query history?', [
            { text: 'Cancel', style: 'cancel' },
            {
                text: 'Clear All', style: 'destructive', onPress: async () => {
                    await clearHistory();
                    setLocalHistory([]);
                    setLocalHistoryCursor(null);
                    setLocalHistoryHasMore(false);
                    setHistoryStats({ allTime: 0, thisMonth: 0, completed: 0, avgResults: 0 });
                }
            },
        ]);
    }, [clearHistory]);

    // Sync state with userProfile from context
    // Listen for profile photo changes from AuthContext
    useEffect(() => {
        const photoUrl = userProfile?.profilePhoto || userProfile?.photoURL || user?.photoURL || user?.profilePhoto || '';
        if (photoUrl) {
            setProfilePhoto(photoUrl);
        }
    }, [userProfile?.profilePhoto, userProfile?.photoURL, user?.photoURL, user?.profilePhoto]);

    // Deep-link support: navigateTo('Profile', { payload: { tab: 'queryHistory' } }) opens the right tab
    useEffect(() => {
        if (routePayload?.tab && VALID_TABS.includes(routePayload.tab)) {
            setActiveTab(routePayload.tab);
        }
    }, [routePayload?.tab]);

    // Load first page of history whenever the tab is opened
    useEffect(() => {
        if (activeTab === 'queryHistory') {
            loadHistoryFirstPage();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    useEffect(() => {
        if (userProfile) {
            setUsername(userProfile.username || '');
            originalUsername.current = userProfile.username || '';
            setEmail(userProfile.email || '');
            setPhone(userProfile.phoneNumber || '');
            setProfilePhoto(userProfile.profilePhoto || userProfile.photoURL || user?.photoURL || '');

            // Use effectiveUserProfile so admin preview mode is reflected
            const profile = effectiveUserProfile || userProfile;
            if (profile.subscription) {
                const sub = profile.subscription;
                setSubscriptionPlan(sub.planType || (sub.subscriptionStatus === 'active' ? 'ACTIVE' : 'FREE'));
                setSubscriptionInfo({
                    status: sub.subscriptionStatus || 'free',
                    currentPeriodEnd: sub.currentPeriodEnd || null,
                    cancelAtPeriodEnd: sub.cancelAtPeriodEnd || false,
                    cancelAt: sub.cancelAt || null,
                    isPreview: sub._isPreview || false,
                });
            } else {
                setSubscriptionPlan('FREE');
                setSubscriptionInfo(null);
            }

            if (userProfile.preferences) {
                const prefs = userProfile.preferences;
                setKellyCriterion(prefs.kellyCriterion?.toString() || '0.25');
                setLegsPerPage(prefs.legsPerPage?.toString() || '10');
                setMinBooksStraights(prefs.minBooksStraights?.toString() || '5');
                setMinBooksSGP(prefs.minBooksSGP?.toString() || '5');
                setMinBooksMyBooks(prefs.minBooksMyBooks?.toString() || '3');
                setUnitSize(`$${prefs.unitSize || 10}`);
                setShowAllMath(prefs.showAllMath ?? true);
                setShowNerdMathByDefault(prefs.showNerdMathByDefault ?? false);
                setShowAltsExpanded(prefs.showAltsExpanded !== undefined ? prefs.showAltsExpanded : true); // ⭐ Default ON
                setFilterPositiveEvOnly(prefs.filterPositiveEvOnly ?? false);
                setDefaultBetMode(prefs.defaultBetMode || 'infer');
                setAutoSwitchMode(prefs.autoSwitchMode !== undefined ? prefs.autoSwitchMode : true);
                // v1.20.6 — One-time migration from legacy preferences.
                //
                // (a) `defaultSortMethod === 'mybooks'` is no longer a valid value (the
                //     option was removed from the Display tab). Promote it to
                //     `defaultBookSort: 'mybooks'` and reset `defaultSortMethod` to 'ev'.
                //
                // (b) `myBooksPriority: true` users are promoted to `defaultBookSort: 'mybooks'`,
                //     BUT ONLY when `defaultBookSort` is missing from Firestore entirely
                //     (the user has truly never set it). If `defaultBookSort` is already
                //     defined — even as 'ev' — the user has explicitly chosen All Books,
                //     and we must respect that choice. Otherwise the legacy
                //     `myBooksPriority: true` value would re-stomp their save on every
                //     hydrate, since the save payload no longer writes `myBooksPriority`.
                const _legacyMyBooksSort = prefs.defaultSortMethod === 'mybooks';
                const _hasExplicitBookSort = Object.prototype.hasOwnProperty.call(prefs, 'defaultBookSort');
                setDefaultSortMethod(_legacyMyBooksSort ? 'ev' : (prefs.defaultSortMethod || 'ev'));
                if (_legacyMyBooksSort) {
                    setDefaultBookSort('mybooks');
                } else if (_hasExplicitBookSort) {
                    setDefaultBookSort(prefs.defaultBookSort || 'ev');
                } else if (prefs.myBooksPriority === true) {
                    setDefaultBookSort('mybooks');
                } else {
                    setDefaultBookSort('ev');
                }
                setBackgroundQueryMode(prefs.backgroundQueryMode || false);
                setDoneSoundEnabled(prefs.doneSoundEnabled ?? true);
                setShowEnvPill(prefs.showEnvPill !== undefined ? prefs.showEnvPill : true);
                setSelectedSportsbooks(prefs.selectedSportsbooks || []);
                setSportsbookLimits(prefs.sportsbookLimits || {});
                setEnabledSportsbookLimits(prefs.enabledSportsbookLimits || {});
                setMyBooks(prefs.myBooks || []);
                // Legacy: `myBooksPriority` preference is deprecated — `defaultBookSort` is the single switch.
                const _policy = prefs.consensusBookPolicy;
                setConsensusBookPolicy(
                    _policy === 'include_all' || _policy === 'custom' || _policy === 'default'
                        ? _policy
                        : 'default'
                );
                setConsensusExcludedBooks(Array.isArray(prefs.consensusExcludedBooks) ? prefs.consensusExcludedBooks : []);
            }
        }
    }, [userProfile, effectiveUserProfile]);

    // Page load animation
    useEffect(() => {
        // Main page animation
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 1,
                duration: 1200,
                easing: Easing.inOut(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: 0,
                duration: 1000,
                easing: Easing.out(Easing.back(1.1)),
                useNativeDriver: true,
            })
        ]).start();

        // Staggered section animations
        const sectionAnimations = [
            { ref: profileSectionOpacity, delay: 200 },
            { ref: accountSectionOpacity, delay: 400 },
            { ref: preferencesSectionOpacity, delay: 600 },
            { ref: actionButtonsOpacity, delay: 800 }
        ];

        sectionAnimations.forEach(({ ref, delay }) => {
            Animated.timing(ref, {
                toValue: 1,
                duration: 600,
                delay: delay,
                easing: Easing.out(Easing.cubic),
                useNativeDriver: true,
            }).start();
        });
    }, []);

    // Register exit animation with navigation context
    useEffect(() => {
        if (Platform.OS === 'web') {
            window.profileExitAnimation = exitAnimation;
        }
        
        return () => {
            if (Platform.OS === 'web') {
                delete window.profileExitAnimation;
            }
        };
    }, []);

    // Load betslips
    useEffect(() => {
        if (!user?.uid) return;
        try {
            const ref = collection(db, 'users', user.uid, 'betslips');
            const q = fsQuery(ref, orderBy('createdAt', 'desc'));
            const unsub = onSnapshot(q, (snap) => {
                const list = [];
                snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
                setBetSlips(list);
                setBetSlipsLoading(false);
            });
            return () => unsub();
        } catch {
            setBetSlipsLoading(false);
        }
    }, [user?.uid]);

    // Load saved queries
    useEffect(() => {
        const fetchSavedQueries = async () => {
            if (!user?.uid) {
                setSavedQueriesLoading(false);
                return;
            }
            
            try {
                const q = fsQuery(
                    collection(db, 'saved_queries'),
                    where('uid', '==', user.uid),
                    orderBy('createdAt', 'desc')
                );
                const snapshot = await getDocs(q);
                const queries = snapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));
                setSavedQueries(queries);
                setSavedQueriesLoading(false);
            } catch (error) {
                console.error('[ProfilePage] Error fetching saved queries:', error);
                setSavedQueriesLoading(false);
            }
        };
        
        fetchSavedQueries();
    }, [user?.uid]);

    // Load watched fixtures
    useEffect(() => {
        const fetchWatchedFixtures = async () => {
            if (!user?.uid) {
                setWatchedFixturesLoading(false);
                return;
            }
            
            try {
                const q = fsQuery(
                    collection(db, 'query_watchers'),
                    where('uid', '==', user.uid),
                    where('status', '==', 'active'),
                    orderBy('createdAt', 'desc')
                );
                const snapshot = await getDocs(q);
                const fixtures = snapshot.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                }));
                setWatchedFixtures(fixtures);
                setWatchedFixturesLoading(false);
            } catch (error) {
                console.error('[ProfilePage] Error fetching watched fixtures:', error);
                setWatchedFixturesLoading(false);
            }
        };
        
        fetchWatchedFixtures();
    }, [user?.uid]);

    // Load user data on component mount
    useEffect(() => {
        if (user) {
            setUsername(user.username || '');
            originalUsername.current = user.username || '';
            setEmail(user.email || '');
            setPhone(user.phoneNumber || '');
            setProfilePhoto(user.profilePhoto || user.photoURL || '');

            // Load preferences
            if (user.preferences) {
                const prefs = user.preferences;
                setKellyCriterion(prefs.kellyCriterion || '0.25');
                setLegsPerPage(prefs.legsPerPage || '10');
                setMinBooksStraights(prefs.minBooksStraights || '5');
                setMinBooksSGP(prefs.minBooksSGP || '5');
                setMinBooksMyBooks(prefs.minBooksMyBooks?.toString() || '3');
                setUnitSize(prefs.unitSize || '$10');
                setShowAllMath(prefs.showAllMath ?? true);
                setShowNerdMathByDefault(prefs.showNerdMathByDefault ?? false);
                setShowAltsExpanded(prefs.showAltsExpanded !== undefined ? prefs.showAltsExpanded : true); // ⭐ Default ON
                setFilterPositiveEvOnly(prefs.filterPositiveEvOnly ?? false);
                setDoneSoundEnabled(prefs.doneSoundEnabled ?? true);
                setDefaultBetMode(prefs.defaultBetMode || 'infer');
                setAutoSwitchMode(prefs.autoSwitchMode !== undefined ? prefs.autoSwitchMode : true);
                setSelectedSportsbooks(prefs.selectedSportsbooks || []);
                setSportsbookLimits(prefs.sportsbookLimits || {});
                setEnabledSportsbookLimits(prefs.enabledSportsbookLimits || {});
                const _policy2 = prefs.consensusBookPolicy;
                setConsensusBookPolicy(
                    _policy2 === 'include_all' || _policy2 === 'custom' || _policy2 === 'default'
                        ? _policy2
                        : 'default'
                );
                setConsensusExcludedBooks(Array.isArray(prefs.consensusExcludedBooks) ? prefs.consensusExcludedBooks : []);
            }
        }
    }, [user]);

    // Load query usage — refresh on login and every time user navigates to this screen
    useEffect(() => {
        const loadQueryUsage = async () => {
            if (!user?.uid || currentScreen !== 'Profile') return;
            
            setQueryUsageLoading(true);
            try {
                const isLocalhost = Platform.OS === 'web' && 
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                const base = isLocalhost
                    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
                    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
                
                const response = await fetch(`${base}/getQueryUsage`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uid: user.uid })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setQueryUsage(data);
                }
            } catch (error) {
                console.error('[ProfilePage] Error loading query usage:', error);
            } finally {
                setQueryUsageLoading(false);
            }
        };
        
        loadQueryUsage();
    }, [user?.uid, currentScreen]);

    // Animated Input Component with internal focus handling
    const AnimatedInput = useCallback(({ value, onChangeText, placeholder, style }) => {
        const focusT = useRef(new Animated.Value(0)).current;

        const onFocus = () => {
            Animated.timing(focusT, {
                toValue: 1,
                duration: 1500,
                easing: Easing.bezier(0.22, 1, 0.36, 1),
                useNativeDriver: true
            }).start();
        };

        const onBlur = () => {
            Animated.timing(focusT, {
                toValue: 0,
                duration: 1500,
                easing: Easing.bezier(0.22, 1, 0.36, 1),
                useNativeDriver: true
            }).start();
        };

        return (
            <View style={[styles.customInputContainer, style]}>
                <GradientOverlay opacity={focusT} borderRadius={4} />
                <GlassBackgroundGradient borderRadius={4} />
                <BlurView intensity={60} tint="dark" style={styles.customInputBlur}>
                    <TextInput
                        style={styles.customInputText}
                        value={value}
                        onChangeText={onChangeText}
                        placeholder={placeholder}
                        placeholderTextColor="#B5B5B5"
                        onFocus={onFocus}
                        onBlur={onBlur}
                    />
                </BlurView>
            </View>
        );
    }, []);

    // Simple Input Component for preferences (no gradients)
    const SimpleInput = useCallback(({ value, onChangeText, placeholder, style }) => (
        <View style={[styles.settingValue, style]}>
            <LinearGradient
                colors={['rgba(255, 255, 255, 0.05)', 'rgba(255, 255, 255, 0.10)']}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
                style={styles.settingValueGradient}
            />
            <TextInput
                style={styles.simpleInputText}
                value={value}
                onChangeText={onChangeText}
                placeholder={placeholder}
            />
        </View>
    ), []);

    const [allSportsbooks, setAllSportsbooks] = useState([]);
    const [showAllBooks, setShowAllBooks] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                const Constants = (await import('expo-constants')).default;
                const Platform = (await import('react-native')).Platform;
                
                // Determine functions URL: use emulator on localhost, production otherwise
                const isLocalhost = Platform.OS === 'web' && 
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                
                const base = isLocalhost
                    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1' // Emulator
                    : (
                        (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                        || (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL)
                        || (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL)
                        || (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL)
                        || 'https://us-central1-bet-optimized-fe922.cloudfunctions.net' // Production
                    );
                const res = await fetch(`${base}/opticOddsProxy?path=${encodeURIComponent('/sportsbooks?limit=200')}`);
                const txt = await res.text();
                let arr = [];
                try { const body = JSON.parse(txt); arr = Array.isArray(body?.data) ? body.data : (Array.isArray(body) ? body : (Array.isArray(body?.items) ? body.items : [])); } catch {}
                const names = arr.map(b => b?.name || b).filter(Boolean);
                
                // Define the 35 active sportsbooks that are queried in the cache (from oddsRefresh.js)
                const activeSportsbooks = [
                    // Tier 1
                    'DraftKings', 'FanDuel', 'BetMGM', 'Caesars', 'ESPNBet',
                    // Tier 2
                    'BetRivers', 'Fanatics', 'BetOnline', 'Bovada', 'HardRock',
                    // Tier 3
                    'Superbook', 'MyBookie', 'GoldenNugget', 'WilliamHill', 'BallyBet',
                    // Tier 4
                    'Pinnacle', 'Betfred', 'BetUS', 'Borgata', 'BetPARX',
                    // Tier 5
                    'Circa Vegas', 'Novig', 'Underdog Sportsbook', 'Sleeper',
                    // Tier 6
                    'Prophet X', 'Kalshi', 'BookMaker', 'Resorts World Bet', 'Tipico',
                    // Tier 7
                    'Rebet', 'Fliff', 'Betr', 'WynnBET', 'STN Sports'
                ];
                
                // Separate into active (available in cache) and coming soon
                const active = names.filter(name => activeSportsbooks.includes(name));
                const comingSoon = names.filter(name => !activeSportsbooks.includes(name));
                
                // Sort active books by tier order
                const sorted = active.sort((a, b) => {
                    const aIdx = activeSportsbooks.indexOf(a);
                    const bIdx = activeSportsbooks.indexOf(b);
                    if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
                    return a.localeCompare(b);
                });
                
                // Append coming soon books at the end (alphabetically sorted)
                const comingSoonSorted = comingSoon.sort((a, b) => a.localeCompare(b));
                const allBooks = [...sorted, ...comingSoonSorted];
                
                setAllSportsbooks(allBooks);
            } catch {}
        })();
    }, []);

    // Sportsbook selection handlers - UNIFIED: both filter and limit
    const toggleSportsbook = (sportsbook) => {
        setSelectedSportsbooks(prev =>
            prev.includes(sportsbook)
                ? prev.filter(s => s !== sportsbook)
                : [...prev, sportsbook]
        );
    };

    const updateSportsbookLimit = (sportsbook, limit) => {
        setSportsbookLimits(prev => ({
            ...prev,
            [sportsbook]: limit
        }));
    };

    const toggleSportsbookLimit = (sportsbook) => {
        setEnabledSportsbookLimits(prev => ({
            ...prev,
            [sportsbook]: !prev[sportsbook]
        }));
    };
    
    // Clear all sportsbook filters
    const clearAllSportsbookFilters = () => {
        setSelectedSportsbooks([]);
    };

    // Profile photo upload function
    const handleProfilePhotoUpload = async () => {
        if (!user?.uid) {
            Alert.alert('Error', 'Please log in again');
            return;
        }

        try {
            const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });
            if (!result.canceled && result.assets && result.assets.length > 0) {
                const file = result.assets[0];
                const fileUri = file.uri;
                const response = await fetch(fileUri);
                const fileBlob = await response.blob();

                const fileExt = file.name.split('.').pop();
                const fileName = `${user.uid}.${fileExt}`;

                const uploadResult = await uploadProfilePhoto(fileBlob, fileName);
                
                if (uploadResult.success) {
                    // Photo URL should be updated via AuthContext state
                    // But we also set local state to ensure immediate UI update
                    setProfilePhoto(uploadResult.photoURL);
                } else {
                    console.error('[ProfilePage] Upload failed:', uploadResult.error);
                    // Error toast already dispatched by uploadProfilePhoto
                }
            }
        } catch (error) {
            console.error('Error uploading profile photo:', error);
            Alert.alert('Error', 'Failed to update profile photo. Please try again.');
        }
    };

    // Profile photo remove function
    const handleProfilePhotoRemove = async () => {
        if (!user?.uid) {
            Alert.alert('Error', 'Please log in again');
            return;
        }
        const result = await removeProfilePhoto();
        if (result.success) {
            setProfilePhoto('');
        }
    };

    // Image picker function (fallback for mobile)
    const pickImage = async () => {
        try {
            // Request permission
            const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

            if (permissionResult.granted === false) {
                Alert.alert("Permission Required", "Permission to access camera roll is required!");
                return;
            }

            // Launch image picker
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Images,
                allowsEditing: true,
                aspect: [1, 1],
                quality: 0.8,
            });

            if (!result.canceled) {
                // For mobile, we'll use the image picker result directly
                setProfilePhoto(result.assets[0].uri);
            }
        } catch (error) {
            Alert.alert("Error", "Failed to pick image. Please try again.");
            console.error('Image picker error:', error);
        }
    };

    const handleUsernameChange = (value) => {
        setUsername(value);
        clearTimeout(usernameCheckTimer.current);
        const trimmed = value.trim();
        if (trimmed.toLowerCase() === originalUsername.current.toLowerCase()) {
            setUsernameStatus(null);
            return;
        }
        if (!trimmed || trimmed.length < 3) {
            setUsernameStatus(trimmed.length > 0 ? 'invalid' : null);
            return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(trimmed)) {
            setUsernameStatus('invalid');
            return;
        }
        setUsernameStatus('checking');
        usernameCheckTimer.current = setTimeout(async () => {
            const available = await authService.checkUsernameAvailable(trimmed);
            setUsernameStatus(available === null ? null : (available ? 'available' : 'taken'));
        }, 500);
    };

    const [usernameSaving, setUsernameSaving] = useState(false);
    const saveUsername = async () => {
        const trimmed = username.trim();
        if (!user?.uid || trimmed.toLowerCase() === originalUsername.current.toLowerCase()) return;
        setUsernameSaving(true);
        try {
            const result = await authService.updateUsername(user.uid, trimmed);
            if (result?.error) {
                Alert.alert('Error', result.error);
            } else {
                originalUsername.current = trimmed;
                setUsernameStatus(null);
            }
        } catch (e) {
            Alert.alert('Error', 'Failed to save username');
        } finally {
            setUsernameSaving(false);
        }
    };

    // Auto-save function (called on any setting change)
    const performSave = async () => {
        if (!user?.uid) {
            console.log('[Settings] No user logged in, skipping save');
            return;
        }

        setSaving(true);
        setSaveStatus('saving');

        const updatedUserData = {
            email,
            phoneNumber: phone,
            profilePhoto,
            preferences: {
                kellyCriterion: parseFloat(kellyCriterion) || 0.25,
                legsPerPage: parseInt(legsPerPage) || 10,
                minBooksStraights: parseInt(minBooksStraights) || 1,
                minBooksSGP: parseInt(minBooksSGP) || 1,
                minBooksMyBooks: parseInt(minBooksMyBooks) || 1,
                unitSize: parseInt(String(unitSize).replace('$', '')) || 10,
                showAllMath,
                showNerdMathByDefault,
                showAltsExpanded, // ⭐ NEW: Show alternate lines expanded
                filterPositiveEvOnly, // v1.12.x — persist the +EV-only filter across sessions
                defaultBetMode,
                autoSwitchMode,
                defaultSortMethod,
                defaultBookSort,
                backgroundQueryMode,
                doneSoundEnabled,
                showEnvPill,
                selectedSportsbooks,
                sportsbookLimits,
                enabledSportsbookLimits,
                myBooks,
                consensusBookPolicy,
                consensusExcludedBooks,
                tickerLeagues: tickerLeagues || DEFAULT_TICKER_LEAGUES,
            }
        };

        try {
            const result = await saveUserProfile(updatedUserData);
            if (result.success) {
                setSaveStatus('saved');
                console.log('[Settings] ✅ Auto-saved preferences');
                // Reset to idle after 2 seconds
                setTimeout(() => setSaveStatus('idle'), 2000);
            } else {
                setSaveStatus('error');
                console.error('[Settings] ❌ Save failed:', result.error);
                setTimeout(() => setSaveStatus('idle'), 3000);
            }
        } catch (error) {
            setSaveStatus('error');
            console.error('[Settings] ❌ Save error:', error);
            setTimeout(() => setSaveStatus('idle'), 3000);
        } finally {
            setSaving(false);
        }
    };

    // Debounced auto-save (waits 800ms after last change before saving)
    const debouncedSave = useCallback(
        debounce(() => {
            performSave();
        }, 800),
        [user?.uid, email, phone, profilePhoto, kellyCriterion, legsPerPage, 
         minBooksStraights, minBooksSGP, minBooksMyBooks, unitSize, showAllMath, showAltsExpanded, filterPositiveEvOnly, defaultBetMode, 
         autoSwitchMode, defaultSortMethod, defaultBookSort, backgroundQueryMode, doneSoundEnabled, showEnvPill, selectedSportsbooks, 
         sportsbookLimits, enabledSportsbookLimits, myBooks, consensusBookPolicy, consensusExcludedBooks, tickerLeagues]
    );

    // Trigger auto-save when settings change (skip initial load)
    const isInitialMount = useRef(true);
    useEffect(() => {
        // Skip the initial mount to avoid saving on page load
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        
        // Also skip if we haven't loaded the user profile yet
        if (!userProfile) return;
        
        console.log('[Settings] Setting changed, triggering auto-save...');
        debouncedSave();
        
        // Cleanup
        return () => debouncedSave.cancel();
    }, [kellyCriterion, legsPerPage, minBooksStraights, minBooksSGP, unitSize, 
        showAllMath, showAltsExpanded, filterPositiveEvOnly, defaultBetMode, autoSwitchMode, defaultSortMethod, defaultBookSort,
        backgroundQueryMode, doneSoundEnabled, showEnvPill, selectedSportsbooks, sportsbookLimits, 
        enabledSportsbookLimits, myBooks, consensusBookPolicy, consensusExcludedBooks]);

    // Legacy handleSave for manual trigger if needed
    const handleSave = performSave;

    // Exit animation function
    const exitAnimation = (callback) => {
        setIsExiting(true);
        Animated.parallel([
            Animated.timing(pageOpacity, {
                toValue: 0,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            }),
            Animated.timing(pageTranslateY, {
                toValue: -30,
                duration: 600,
                easing: Easing.in(Easing.cubic),
                useNativeDriver: true,
            })
        ]).start(() => {
            if (callback) callback();
        });
    };

    // Logout function
    const handleLogout = () => {
        startLogout(); // Trigger loading screen
        exitAnimation(() => {
            logout();
            navigateTo('SignIn');
        });
    };


        return (
        <ProfilePageView
            AnimatedInput={AnimatedInput}
            activeSettingsSection={activeSettingsSection}
            activeTab={activeTab}
            adminPreviewPlan={adminPreviewPlan}
            allSportsbooks={allSportsbooks}
            backgroundQueryMode={backgroundQueryMode}
            betSlips={betSlips}
            betSlipsLoading={betSlipsLoading}
            clearAllSportsbookFilters={clearAllSportsbookFilters}
            consensusBookPolicy={consensusBookPolicy}
            consensusBooksSearch={consensusBooksSearch}
            consensusExcludedBooks={consensusExcludedBooks}
            defaultBookSort={defaultBookSort}
            defaultSortMethod={defaultSortMethod}
            dfsConsensusExclusionEnabled={dfsConsensusExclusionEnabled}
            doneSoundEnabled={doneSoundEnabled}
            email={email}
            filterBooksExpanded={filterBooksExpanded}
            filterBooksSearch={filterBooksSearch}
            filterPositiveEvOnly={filterPositiveEvOnly}
            handleClearHistory={handleClearHistory}
            handleDeleteHistoryEntry={handleDeleteHistoryEntry}
            handleLogout={handleLogout}
            handleProfilePhotoRemove={handleProfilePhotoRemove}
            handleProfilePhotoUpload={handleProfilePhotoUpload}
            handleUsernameChange={handleUsernameChange}
            historyStats={historyStats}
            injuryGroupBy={injuryGroupBy}
            injuryMinSeverity={injuryMinSeverity}
            injuryTickerLeagues={injuryTickerLeagues}
                                    isAdmin={isAdmin}
            isInjuryTickerEnabled={isInjuryTickerEnabled}
            isTickerEnabled={isTickerEnabled}
            kellyCriterion={kellyCriterion}
            legsPerPage={legsPerPage}
            loadHistoryNextPage={loadHistoryNextPage}
            localHistory={localHistory}
            localHistoryHasMore={localHistoryHasMore}
            localHistoryLoading={localHistoryLoading}
            minBooksMyBooks={minBooksMyBooks}
            minBooksSGP={minBooksSGP}
            minBooksStraights={minBooksStraights}
            myBooks={myBooks}
            myBooksExpanded={myBooksExpanded}
            myBooksSearch={myBooksSearch}
            navigateTo={navigateTo}
            pageOpacity={pageOpacity}
            pageTranslateY={pageTranslateY}
            pickImage={pickImage}
            profilePhoto={profilePhoto}
                                    queryUsage={queryUsage}
            queryUsageLoading={queryUsageLoading}
            saveStatus={saveStatus}
            saveUsername={saveUsername}
            savedQueries={savedQueries}
            savedQueriesLoading={savedQueriesLoading}
            sbMode={sbMode}
            selectedSportsbooks={selectedSportsbooks}
            setActiveSettingsSection={setActiveSettingsSection}
            setActiveTab={setActiveTab}
            setBackgroundQueryMode={setBackgroundQueryMode}
            setConsensusBookPolicy={setConsensusBookPolicy}
            setConsensusBooksSearch={setConsensusBooksSearch}
            setConsensusExcludedBooks={setConsensusExcludedBooks}
            setDefaultBookSort={setDefaultBookSort}
            setDefaultSortMethod={setDefaultSortMethod}
            setDoneSoundEnabled={setDoneSoundEnabled}
            setEmail={setEmail}
            setFilterBooksExpanded={setFilterBooksExpanded}
            setFilterBooksSearch={setFilterBooksSearch}
            setFilterPositiveEvOnly={setFilterPositiveEvOnly}
            setKellyCriterion={setKellyCriterion}
            setLegsPerPage={setLegsPerPage}
            setMinBooksMyBooks={setMinBooksMyBooks}
            setMinBooksSGP={setMinBooksSGP}
            setMinBooksStraights={setMinBooksStraights}
            setMyBooks={setMyBooks}
            setMyBooksExpanded={setMyBooksExpanded}
            setMyBooksSearch={setMyBooksSearch}
            setSavedQueries={setSavedQueries}
            setSbMode={setSbMode}
            setShowAllMath={setShowAllMath}
            setShowAltsExpanded={setShowAltsExpanded}
            setShowEnvPill={setShowEnvPill}
            setShowNerdMathByDefault={setShowNerdMathByDefault}
            setUnitSize={setUnitSize}
            setWatchedFixtures={setWatchedFixtures}
            showAllMath={showAllMath}
            showAltsExpanded={showAltsExpanded}
            showEnvPill={showEnvPill}
            showLiveScores={showLiveScores}
            showNerdMathByDefault={showNerdMathByDefault}
            showTickerSGPIndicator={showTickerSGPIndicator}
                                    subscriptionInfo={subscriptionInfo}
            subscriptionPlan={subscriptionPlan}
            tickerLeagues={tickerLeagues}
            tickerNameFormat={tickerNameFormat}
            tickerSGPOnly={tickerSGPOnly}
            toggleSportsbook={toggleSportsbook}
            toggleTicker={toggleTicker}
            totalHeaderHeight={totalHeaderHeight}
            unitSize={unitSize}
            updateInjuryGroupBy={updateInjuryGroupBy}
            updateInjuryMinSeverity={updateInjuryMinSeverity}
            updateInjuryTickerEnabled={updateInjuryTickerEnabled}
            updateInjuryTickerLeagues={updateInjuryTickerLeagues}
            updateShowLiveScores={updateShowLiveScores}
            updateShowTickerSGPIndicator={updateShowTickerSGPIndicator}
            updateTickerLeagues={updateTickerLeagues}
            updateTickerNameFormat={updateTickerNameFormat}
            updateTickerSGPOnly={updateTickerSGPOnly}
            user={user}
            username={username}
            usernameSaving={usernameSaving}
            usernameStatus={usernameStatus}
            watchedFixtures={watchedFixtures}
            watchedFixturesLoading={watchedFixturesLoading}
        />
    );
};



export default ProfilePage;
